import type { JobType } from '@lib/enums/job-type.enum'
import type { TreeMarkColor } from '@lib/enums/tree-mark.enum'

/**
 * How species and jobs are DRAWN.
 *
 * Kept in the web app rather than in `@lib` on purpose: the vocabulary is
 * shared (it lives in `@lib/enums`), but Tailwind class names and SVG outlines
 * are presentation, and pushing them into the shared contract would make the
 * API server carry the styling of a map it never renders.
 *
 * Every class name is written out in full. Tailwind v4 scans source text, so a
 * class assembled at runtime — `fill-map-${species}` — would simply never be
 * generated, and the pin would render with no color at all.
 */

export interface MarkColorStyle {
  fillClass: string
  /** For legend swatches and list rows, where the glyph is drawn in text flow. */
  textClass: string
}

/**
 * The palette, one entry per `TreeMarkColor`.
 *
 * This table is the reason the color set is closed. Tailwind scans source for
 * class names, so every one has to appear here as a literal — and a species
 * table that let somebody type a hex would produce pins with no color at all.
 *
 * Hue identifies a species; SHAPE identifies it a second time, which is what
 * keeps a legend legible to a color-blind reader, in the grayscale print these
 * maps end up in, and at the size a pin renders on a phone. That is also why
 * two species are allowed to share a color.
 */
export const TREE_MARK_COLOR_STYLES: Record<TreeMarkColor, MarkColorStyle> = {
  ORANGE: { fillClass: 'fill-map-orange', textClass: 'text-map-orange' },
  VIOLET: { fillClass: 'fill-map-violet', textClass: 'text-map-violet' },
  GREEN: { fillClass: 'fill-map-green', textClass: 'text-map-green' },
  SLATE: { fillClass: 'fill-map-slate', textClass: 'text-map-slate' },
  ROSE: { fillClass: 'fill-map-rose', textClass: 'text-map-rose' },
  MAGENTA: { fillClass: 'fill-map-magenta', textClass: 'text-map-magenta' },
  GREY: { fillClass: 'fill-map-grey', textClass: 'text-map-grey' },
  TEAL: { fillClass: 'fill-map-teal', textClass: 'text-map-teal' },
  BLUE: { fillClass: 'fill-map-blue', textClass: 'text-map-blue' },
  CRIMSON: { fillClass: 'fill-map-crimson', textClass: 'text-map-crimson' },
  OLIVE: { fillClass: 'fill-map-olive', textClass: 'text-map-olive' },
  BROWN: { fillClass: 'fill-map-brown', textClass: 'text-map-brown' },
}

/**
 * The four-to-seven character text stamped on a job badge. A pin can carry all
 * four at once, so the strip has roughly 100px to work with at the zoom where
 * badges appear — the full label does not fit and abbreviating is not optional.
 */
export const JOB_TYPE_ABBREVIATIONS: Record<JobType, string> = {
  CROWN_CLEANING: 'CLEAN',
  CROWN_RAISE: 'RAISE',
  CROWN_RESTORATION: 'RESTORE',
  CROWN_THIN: 'THIN',
  STRUCTURAL_CLEARANCE: 'CLEAR',
  OTHER: 'OTHER',
}

export const JOB_TYPE_BG_CLASSES: Record<JobType, string> = {
  CROWN_CLEANING: 'bg-map-clean',
  CROWN_RAISE: 'bg-map-raise',
  CROWN_RESTORATION: 'bg-map-restore',
  CROWN_THIN: 'bg-map-thin',
  STRUCTURAL_CLEARANCE: 'bg-map-clearance',
  OTHER: 'bg-map-other',
}
